import { useEffect, useState } from "react";
import { Users, Plus, Pencil, Trash2, Phone } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

export const CustomerButton = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    try {
      const payload = {
        name: form.name, address: form.address, phone: form.phone,
        email: form.email, contact: form.contact,
      };
      if (form.id) await api.put(`/customers/${form.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(t("customers.saved"));
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(t("customers.deleteConfirm", { name: c.name }))) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(t("customers.removed"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <Button
        data-testid="customer-button"
        onClick={() => setOpen(true)}
        className="h-9 bg-amber-500 px-3 font-semibold text-slate-900 transition-colors duration-200 hover:bg-amber-400"
      >
        <Users className="mr-1.5 h-4 w-4" /> {t("customers.button")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-dialog" className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">{t("customers.dialogTitle")}</DialogTitle>
          </DialogHeader>

          {leader && (
            <Button
              data-testid="customer-new-button"
              onClick={() => setForm({ name: "", address: "", phone: "", email: "", contact: "" })}
              className="h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Ny kund
            </Button>
          )}

          {form && (
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="customer-form">
              <div><L>{t("customers.name")}</L>
                <Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10" />
              </div>
              <div><L>{t("customers.address")}</L>
                <Input data-testid="customer-address-input" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 h-10" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><L>{t("customers.phone")}</L>
                  <Input data-testid="customer-phone-input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-10" />
                </div>
                <div><L>{t("customers.contact")}</L>
                  <Input data-testid="customer-contact-input" value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1 h-10" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button data-testid="customer-save-button" onClick={save} className="h-10 bg-blue-600 text-white hover:bg-blue-700">{t("common.save")}</Button>
                <Button variant="outline" onClick={() => setForm(null)} className="h-10 bg-white">{t("common.cancel")}</Button>
              </div>
            </div>
          )}

          <div className="space-y-1">
            {customers.map((c) => (
              <div key={c.id} data-testid={`customer-row-${c.id}`} className="flex items-center gap-2 rounded-md border border-slate-200 p-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{c.name}</p>
                  <p className="truncate text-xs text-slate-500">{c.address || "–"}</p>
                </div>
                {c.phone && (
                  <a href={`tel:${c.phone.replace(/\s/g, "")}`} data-testid={`customer-call-${c.id}`} className="rounded p-2 text-green-700 transition-colors duration-200 hover:bg-green-50">
                    <Phone className="h-4 w-4" />
                  </a>
                )}
                {leader && (
                  <>
                    <button data-testid={`customer-edit-${c.id}`} onClick={() => setForm(c)} className="rounded p-2 text-slate-600 transition-colors duration-200 hover:bg-slate-100">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`customer-delete-${c.id}`} onClick={() => remove(c)} className="rounded p-2 text-red-600 transition-colors duration-200 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerButton;
